import React from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { selectActiveFacebookPage } from '@mm/state/selectors/facebookPageSelector';
import { Alert, Spinner } from '@mm/components';
import {
  ChannelName,
  ChannelsActiveMap,
  isChannelName,
} from '@mm/state/model/ChatbotModel';

import { useBotChannels } from '../../../../../common/hooks/use-bot-channels';
import ConnectedPageModel from '@mm/state/model/ConnectedPageModel';
import { hasConnectedRealFacebookPageSelector } from '@mm/state/selectors/userSelector';

type Props = {
  className?: string;
  channel: ChannelName | ChannelName[];
  type: string;
  verb?: string;
};

const getText = (channel = '', type = '', verb = 'sent', multi = false) => {
  return `${channel} ${
    multi ? 'are' : 'is'
  } turned off for this bot. This ${type} won’t be ${verb} until you turn ${
    multi ? 'these' : 'this'
  } channel${multi ? 's' : ''} back on.`.replace('{channel}', channel);
};

export const InactiveChannelAlert = ({
  className,
  channel,
  type,
  verb,
}: Props) => {
  const { busy, toggleChannel, toggleChannels, formatChannelName } =
    useBotChannels();

  if (!channel) {
    return <></>;
  }

  const multi = Array.isArray(channel);

  let formatted = '';

  if (Array.isArray(channel)) {
    formatted = channel.filter(isChannelName).map(formatChannelName).join(', ');
  }

  if (isChannelName(channel)) {
    formatted = formatChannelName(channel);
  }

  const text = getText(formatted, type, verb, multi);

  const onClick = async () => {
    if (isChannelName(channel)) {
      await toggleChannel(channel, true);
    }
  };

  const onClickMulti = async () => {
    if (Array.isArray(channel) && channel.every(isChannelName)) {
      await toggleChannels(channel, true);
    }
  };

  return (
    <Alert
      className={classNames(className)}
      borderRadius={0}
      type="warning"
      showCloseIcon={false}
    >
      <div className="facebook-page-alerts__alert">
        <span>{text}</span>
        &nbsp;
        {busy && (
          <span className="facebook-page-alerts__action">
            <Spinner
              standalone
              size={13}
              className="facebook-page-alerts__spinner"
            />
          </span>
        )}
        {!busy && (
          <span
            className="facebook-page-alerts__action"
            onClick={multi ? onClickMulti : onClick}
          >
            Reactivate {formatted}
          </span>
        )}
      </div>
    </Alert>
  );
};

const useAvailableChannels = () => {
  const connectedPage = useSelector(selectActiveFacebookPage);
  const isUserPageConnected = useSelector(hasConnectedRealFacebookPageSelector);

  const { smsActivated, instagramActivated, webChatActivated } =
    connectedPage || ({} as ConnectedPageModel);

  return [
    isUserPageConnected && 'facebook',
    smsActivated && 'sms',
    instagramActivated && 'instagram',
    webChatActivated && 'webChat',
  ]
    .filter(Boolean)
    .filter(isChannelName);
};

export const InactiveChannelAlertSmart = ({
  className,
  type,
  verb,
  ...rest
}: Omit<Props, 'channel'> & ChannelsActiveMap) => {
  const availableChannels = useAvailableChannels();

  const { validateChannels } = useBotChannels();

  const channels = Object.entries(rest)
    .filter(([_, v]) => !!v)
    .map(([v]) => v)
    .filter(isChannelName)
    .filter(channel => availableChannels.includes(channel));

  const invalid = validateChannels(channels);

  if (invalid.length > 2) {
    return (
      <InactiveChannelAlert
        channel={invalid}
        type={type}
        verb={'working for these channels'}
      />
    );
  }

  return (
    <>
      {invalid.map(channel => (
        <InactiveChannelAlert
          key={channel}
          className={className}
          channel={channel}
          type={type}
          verb={verb}
        />
      ))}
    </>
  );
};
